import { Box, Button, Checkbox, Stack, Typography } from '@mui/material';
import {
  ApiError,
  DATE_TIME_WITH_DASH_FORMAT_FNS,
  ExportMarkbookArrangeAssessmentsBy,
  ExportMarkbookArrangeStudentsBy,
  ExportMarkbookInclude,
  ExportMarkbookParams,
  GetMarkbookParams,
  MarkbookArrangeBy,
  useExportMarkbook,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { ExportStatus, PageExport } from '@schooly/components/export';
import { ConductTypeSelectMultiple, toggleArrayProperty } from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import { DELAY_BEFORE_HIDE_ICON_DONE } from '@schooly/constants';
import { CheckboxLabel, CheckboxSquareIconButton } from '@schooly/style';
import { CheckboxSquareLabel } from '@schooly/style';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FC, useCallback, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import { downloadFile } from '../../../../utils/downloadFile';

export type MarkbookPreviewExportProps = { params: GetMarkbookParams };

export const MarkbookPreviewExport: FC<MarkbookPreviewExportProps> = ({ params }) => {
  const { formatMessage } = useIntl();
  const { showNotification, showError } = useNotifications();
  const { isLoading, mutateAsync: exportMarkbook } = useExportMarkbook();

  const [exportStatus, setExportStatus] = useState<ExportStatus | null>(null);

  const resetExportStatus = useCallback(() => {
    setTimeout(() => setExportStatus(null), DELAY_BEFORE_HIDE_ICON_DONE);
  }, []);

  const handleError = useCallback(() => {
    showError({ message: formatMessage({ id: 'error-ExportProblems' }) });
    setExportStatus('error');
    resetExportStatus();
  }, [formatMessage, resetExportStatus, showError]);

  const handleSuccess = useCallback(() => {
    showNotification({
      message: formatMessage({ id: 'assessments-markbook-exportSuccess' }),
      type: 'success',
    });
    setExportStatus('success');
    resetExportStatus();
  }, [formatMessage, resetExportStatus, showNotification]);

  return (
    <PageExport
      renderMenuContent={(close) => (
        <MarkbookPreviewExportForm
          onSuccess={handleSuccess}
          onError={handleError}
          onExport={exportMarkbook}
          params={params}
          close={close}
        />
      )}
      exportStatus={exportStatus}
      isLoading={isLoading}
    />
  );
};

type MarkbookPreviewExportFormProps = {
  onSuccess: () => void;
  onError: () => void;
  onExport: UseMutateAsyncFunction<ArrayBuffer, ApiError, ExportMarkbookParams>;
  params: GetMarkbookParams;
  close: () => void;
};

const MarkbookPreviewExportForm: FC<MarkbookPreviewExportFormProps> = ({
  onSuccess,
  onError,
  onExport,
  params,
  close,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useAuth();

  const { permissions } = useAuth();
  // Conduct export is disabled until it is ready on BE
  // const canExportConduct = permissions?.includes('conduct_viewer');
  const canExportConduct = false;
  const canExportAttendance = permissions?.includes('attendance_viewer');

  const form = useForm<ExportMarkbookParams>({
    defaultValues: {
      schoolId: params.schoolId,
      filters: params.filters ?? {},
      sort: params.sort,
      include: [ExportMarkbookInclude.Assessments],
      arrangeStudentsBy:
        params.arrangeBy === MarkbookArrangeBy.AgeGroup
          ? ExportMarkbookArrangeStudentsBy.AgeGroup
          : ExportMarkbookArrangeStudentsBy.Group,
      arrangeAssessmentsBy: ExportMarkbookArrangeAssessmentsBy.Assessment,
    },
  });

  const include = form.watch('include');
  const arrangeAssessmentsBy = form.watch('arrangeAssessmentsBy');
  const filters = form.watch('filters');
  const isAttendanceActive = include.includes(ExportMarkbookInclude.Attendance);
  const isConductActive = include.includes(ExportMarkbookInclude.Conduct);
  const isSubjectActive = arrangeAssessmentsBy === ExportMarkbookArrangeAssessmentsBy.Subject;
  const isAssessmentActive = arrangeAssessmentsBy === ExportMarkbookArrangeAssessmentsBy.Assessment;

  const handleExport = useCallback<SubmitHandler<ExportMarkbookParams>>(
    async (formData) => {
      if (!schoolId) return;
      close();

      await onExport(formData, {
        onSuccess: (data) => {
          try {
            const timestamp = format(new Date(), DATE_TIME_WITH_DASH_FORMAT_FNS);
            downloadFile(data, `markbook_${timestamp}.csv`);

            onSuccess();
          } catch (_error) {
            onError();
          }
        },
        onError: onError,
      });
    },
    [close, onError, onExport, onSuccess, schoolId],
  );

  const selectInclude = useCallback(
    (includeOption: ExportMarkbookInclude) => {
      form.setValue('include', toggleArrayProperty(form.getValues('include'), includeOption));
    },
    [form],
  );

  const toggleArrangeAssessmentsBy = useCallback(() => {
    form.setValue(
      'arrangeAssessmentsBy',
      form.getValues('arrangeAssessmentsBy') === ExportMarkbookArrangeAssessmentsBy.Assessment
        ? ExportMarkbookArrangeAssessmentsBy.Subject
        : ExportMarkbookArrangeAssessmentsBy.Assessment,
    );
  }, [form]);

  const resetConductType = useCallback(() => {
    form.setValue('filters', {
      ...form.getValues('filters'),
      conduct_type: [],
    });
  }, [form]);

  return (
    <Box width="100%">
      <form onSubmit={form.handleSubmit(handleExport)}>
        <Stack gap={2} width="100%">
          {(canExportAttendance || canExportConduct) && (
            <Typography variant="h4" color="common.main3">
              {formatMessage({ id: 'assessments-markbook-optionalData' })}
            </Typography>
          )}

          {canExportAttendance && (
            <CheckboxSquareLabel
              active={isAttendanceActive}
              onClick={(e) => {
                e.preventDefault();
                selectInclude(ExportMarkbookInclude.Attendance);
              }}
              label={
                <Typography variant="h3" color="inherit">
                  {formatMessage({ id: 'assessments-markbook-attendanceRate' })}
                </Typography>
              }
              control={<CheckboxSquareIconButton isActive={isAttendanceActive} />}
            />
          )}

          {canExportConduct && (
            <>
              <CheckboxSquareLabel
                active={isConductActive}
                onClick={(e) => {
                  e.preventDefault();
                  if (isConductActive) {
                    resetConductType();
                  }
                  selectInclude(ExportMarkbookInclude.Conduct);
                }}
                label={
                  <Typography variant="h3" color="inherit">
                    {formatMessage({ id: 'conduct' })}
                  </Typography>
                }
                control={<CheckboxSquareIconButton isActive={isConductActive} />}
              />
              {isConductActive && (
                <Controller
                  name="filters.conduct_type"
                  control={form.control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    const hasValues = !!filters.conduct_type?.length;
                    const handleChange = (conductTypeId: string) => {
                      const newValues = toggleArrayProperty(field.value, conductTypeId);
                      field.onChange(newValues);
                    };

                    return (
                      <ConductTypeSelectMultiple
                        schoolId={schoolId}
                        onSelectId={handleChange}
                        selectedValue={field.value ?? []}
                        emptyPlaceholder={formatMessage({ id: 'conduct-ConductType' })}
                        placeholder={formatMessage({ id: 'conduct-ConductType' })}
                        error={fieldState.error}
                        layoutStyleProps={{
                          paddingTop: hasValues ? 2.25 : undefined,
                          ' .dropdownOpened &': {
                            paddingTop: 2.25,
                          },
                        }}
                      />
                    );
                  }}
                />
              )}
            </>
          )}

          <Typography variant="h4" color="common.main3">
            {formatMessage({ id: 'assessments-markbook-groupBy' })}
          </Typography>

          <CheckboxLabel
            active={isAssessmentActive}
            label={formatMessage({ id: 'assessments-markbook-assessment' })}
            control={
              <Checkbox checked={isAssessmentActive} onChange={toggleArrangeAssessmentsBy} />
            }
          />

          <CheckboxLabel
            active={isSubjectActive}
            label={formatMessage({ id: 'assessments-markbook-subject' })}
            control={<Checkbox checked={isSubjectActive} onChange={toggleArrangeAssessmentsBy} />}
          />

          <Button
            variant="outlined"
            type="submit"
            disabled={!schoolId}
            fullWidth
            sx={{ width: '100%' }}
          >
            {formatMessage({ id: 'assessments-markbook-exportToCSV' })}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};
